import {
  RichiestaPreventivoDto,
  RichiestaPreventivoTipo,
  TipoOlioLavorazioni,
  TipoOlioLubrifica,
} from "@/api";
import Typography from "@/elements/Typography";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Box, Chip, Divider, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

type Translate = (key: string, options?: Record<string, unknown>) => string;

type LocaleFormatter = {
  t: Translate;
  ndLabel: string;
  locale: string;
};

function formatEuro(
  value: number | null | undefined,
  { t, ndLabel, locale }: LocaleFormatter
): string {
  if (value == null) return ndLabel;
  const formatted = value.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return t("richiestaPreventivoDetailDialog.valori.euro", {
    value: formatted,
  });
}

function formatLitri(
  value: number | null | undefined,
  { t, ndLabel }: LocaleFormatter
): string {
  if (value == null) return ndLabel;
  return t("richiestaPreventivoDetailDialog.valori.litri", {
    value: value.toFixed(0),
  });
}

function formatNumber(
  value: number | null | undefined,
  { ndLabel, locale }: LocaleFormatter
): string {
  if (value == null) return ndLabel;
  return value.toLocaleString(locale);
}

function tipoLabel(
  tipo: RichiestaPreventivoDto["tipo"],
  { t, ndLabel }: LocaleFormatter
): string {
  switch (tipo) {
    case RichiestaPreventivoTipo.lubrifica:
      return t("richiestaPreventivoDetailDialog.tipi.lubrifica");
    case RichiestaPreventivoTipo.lavorazioniMeccaniche:
      return t("richiestaPreventivoDetailDialog.tipi.lavorazioniMeccaniche");
    default:
      return ndLabel;
  }
}

function tipoOlioLubrificaLabel(
  tipo: RichiestaPreventivoDto["tipoOlioLubrifica"],
  { t, ndLabel }: LocaleFormatter
): string {
  switch (tipo) {
    case TipoOlioLubrifica.idraulico:
      return t("richiestaPreventivoDetailDialog.tipiOlioLubrifica.idraulico");
    case TipoOlioLubrifica.riduttori:
      return t("richiestaPreventivoDetailDialog.tipiOlioLubrifica.riduttori");
    case TipoOlioLubrifica.turbine:
      return t("richiestaPreventivoDetailDialog.tipiOlioLubrifica.turbine");
    case TipoOlioLubrifica.lubrifica:
      return t("richiestaPreventivoDetailDialog.tipiOlioLubrifica.lubrificante");
    case TipoOlioLubrifica.acquaGlicole:
      return t("richiestaPreventivoDetailDialog.tipiOlioLubrifica.acquaGlicole");
    default:
      return ndLabel;
  }
}

function tipoOlioLavorazioniLabel(
  tipo: RichiestaPreventivoDto["tipoOlioLavorazioni"],
  { t, ndLabel }: LocaleFormatter
): string {
  switch (tipo) {
    case TipoOlioLavorazioni.olioIntero:
      return t("richiestaPreventivoDetailDialog.tipiOlioLavorazioni.olioIntero");
    case TipoOlioLavorazioni.lubrorefrigerante:
      return t(
        "richiestaPreventivoDetailDialog.tipiOlioLavorazioni.lubrorefrigerante"
      );
    default:
      return ndLabel;
  }
}

function DetailRow({
  label,
  value,
  emptyLabel,
}: {
  label: string;
  value: React.ReactNode;
  emptyLabel: React.ReactNode;
}) {
  return (
    <>
      <Grid item xs={5}>
        <Typography variant="caption" fontWeight="bold">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={7}>
        <Typography variant="body2">{value ?? emptyLabel}</Typography>
      </Grid>
    </>
  );
}

function SectionTitle({
  children,
  first = false,
}: {
  children: React.ReactNode;
  first?: boolean;
}) {
  return (
    <Grid item xs={12} sx={{ pt: first ? 0.5 : "24px !important" }}>
      <Typography
        variant="caption"
        fontWeight="bold"
        sx={{ textTransform: "uppercase", letterSpacing: 1, color: "#888" }}
      >
        {children}
      </Typography>
      <Divider sx={{ mt: 0.5, mb: 1.5 }} />
    </Grid>
  );
}

export function RichiestaPreventivoDetailContent({
  richiesta,
}: {
  richiesta: RichiestaPreventivoDto;
}) {
  const { t, i18n } = useTranslation();
  const ndLabel = t("valori.nd");
  const formatter = { t, ndLabel, locale: i18n.language };
  const formatAnni = (value: number | null | undefined) => {
    if (value == null) {
      return ndLabel;
    }

    return t("richiestaPreventivoDetailDialog.valori.anni", {
      value,
      unita: value === 1 ? t("unita.anno") : t("unita.anni"),
    });
  };
  const formatOre = (value: number | null | undefined) => {
    if (value == null) {
      return ndLabel;
    }

    return t("richiestaPreventivoDetailDialog.valori.ore", {
      value,
      unita: t("unita.oreAbbreviazione"),
    });
  };
  const formatCo2 = (value: number | null | undefined) => {
    if (value == null) {
      return ndLabel;
    }

    return t("richiestaPreventivoDetailDialog.valori.co2", {
      value: value.toFixed(0),
      unita: t("unita.kgCo2e"),
    });
  };

  const isLubrifica = richiesta.tipo === RichiestaPreventivoTipo.lubrifica;
  const isLavorazioni =
    richiesta.tipo === RichiestaPreventivoTipo.lavorazioniMeccaniche;
  const isLubrorefrigerante =
    richiesta.tipoOlioLavorazioni === TipoOlioLavorazioni.lubrorefrigerante;

  return (
    <Grid container spacing={1.5}>
      {/* Dati richiedente */}
      <SectionTitle first>
        {t("richiestaPreventivoDetailDialog.sezioni.datiRichiedente")}
      </SectionTitle>

      <DetailRow
        label={t("richiestaPreventivoDetailDialog.campi.azienda")}
        value={richiesta.nomeAzienda}
        emptyLabel={ndLabel}
      />
      <DetailRow
        label={t("richiestaPreventivoDetailDialog.campi.richiedente")}
        value={richiesta.nomeRichiedente}
        emptyLabel={ndLabel}
      />
      <DetailRow
        label={t("richiestaPreventivoDetailDialog.campi.posizione")}
        value={richiesta.posizione}
        emptyLabel={ndLabel}
      />
      <DetailRow
        label={t("richiestaPreventivoDetailDialog.campi.email")}
        value={richiesta.email}
        emptyLabel={ndLabel}
      />
      <DetailRow
        label={t("richiestaPreventivoDetailDialog.campi.telefono")}
        value={richiesta.numeroTelefono}
        emptyLabel={ndLabel}
      />
      <DetailRow
        label={t("richiestaPreventivoDetailDialog.campi.lingua")}
        value={richiesta.lingua?.toUpperCase()}
        emptyLabel={ndLabel}
      />
      <DetailRow
        label={t("richiestaPreventivoDetailDialog.campi.ricontatto")}
        value={
          richiesta.vuoleEssereRicontattato === true ? (
            <Box display="flex" alignItems="center" gap={0.5}>
              <CheckCircleOutlineIcon
                fontSize="small"
                sx={{ color: "success.main" }}
              />
              {t("azioni.si")}
            </Box>
          ) : richiesta.vuoleEssereRicontattato === false ? (
            <Box display="flex" alignItems="center" gap={0.5}>
              <HighlightOffIcon
                fontSize="small"
                sx={{ color: "text.disabled" }}
              />
              {t("azioni.no")}
            </Box>
          ) : (
            ndLabel
          )
        }
        emptyLabel={ndLabel}
      />

      {/* Tipo richiesta */}
      <SectionTitle>
        {t("richiestaPreventivoDetailDialog.sezioni.tipoRichiesta")}
      </SectionTitle>

      <DetailRow
        label={t("richiestaPreventivoDetailDialog.campi.tipo")}
        value={
          richiesta.tipo ? (
            <Chip
              label={tipoLabel(richiesta.tipo, formatter)}
              size="small"
              sx={{
                backgroundColor: isLubrifica ? "#e3f2fd" : "#f3e5f5",
                color: isLubrifica ? "#1565c0" : "#6a1b9a",
                fontWeight: 500,
              }}
            />
          ) : (
            ndLabel
          )
        }
        emptyLabel={ndLabel}
      />

      {isLubrifica && (
        <>
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.tipoOlio")}
            value={tipoOlioLubrificaLabel(
              richiesta.tipoOlioLubrifica,
              formatter
            )}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.frequenzaCambioOlio")}
            value={formatAnni(richiesta.frequenzaAnniDiCambioOlio)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.quantitaOlio")}
            value={formatLitri(richiesta.quantitaOlioLubrifica, formatter)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.prezzoOlio")}
            value={formatEuro(richiesta.prezzoOlioLubrifica, formatter)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.pompeCambiate")}
            value={formatNumber(richiesta.numeroPompeCambiateAnno, formatter)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.valvoleCambiate")}
            value={formatNumber(richiesta.numeroValvoleCambiateAnno, formatter)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.oreFermoMacchina")}
            value={formatOre(richiesta.oreFermoMacchinaAnno)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.costoFermoMacchina")}
            value={formatEuro(richiesta.costoOrarioFermoMacchina, formatter)}
            emptyLabel={ndLabel}
          />
          {richiesta.costoUnitarioPompa != null && (
            <DetailRow
              label={t("richiestaPreventivoDetailDialog.campi.costoUnitarioPompa")}
              value={formatEuro(richiesta.costoUnitarioPompa, formatter)}
              emptyLabel={ndLabel}
            />
          )}
          {richiesta.costoUnitarioValvola != null && (
            <DetailRow
              label={t("richiestaPreventivoDetailDialog.campi.costoUnitarioValvola")}
              value={formatEuro(richiesta.costoUnitarioValvola, formatter)}
              emptyLabel={ndLabel}
            />
          )}
        </>
      )}

      {isLavorazioni && (
        <>
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.tipoOlioLavorazioni")}
            value={tipoOlioLavorazioniLabel(
              richiesta.tipoOlioLavorazioni,
              formatter
            )}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.quantitaOlio")}
            value={formatLitri(richiesta.quantitaOlioLavorazioni, formatter)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t(
              "richiestaPreventivoDetailDialog.campi.frequenzaCambioRefrigerante"
            )}
            value={formatAnni(richiesta.frequenzaAnniDiCambioRefrigerante)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.costoLitroOlio")}
            value={formatEuro(richiesta.costoLitroOlio, formatter)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.spesaMediaUtensili")}
            value={formatEuro(richiesta.spesaMediaAnnuaUtensili, formatter)}
            emptyLabel={ndLabel}
          />
          {richiesta.numeroPompeAltaPressioneLavorazioni != null && (
            <DetailRow
              label={t("richiestaPreventivoDetailDialog.campi.numeroPompeAltaPressione")}
              value={formatNumber(richiesta.numeroPompeAltaPressioneLavorazioni, formatter)}
              emptyLabel={ndLabel}
            />
          )}
          {richiesta.costoUnitarioPompaAltaPressione != null && (
            <DetailRow
              label={t("richiestaPreventivoDetailDialog.campi.costoUnitarioPompaAltaPressione")}
              value={formatEuro(richiesta.costoUnitarioPompaAltaPressione, formatter)}
              emptyLabel={ndLabel}
            />
          )}
        </>
      )}

{/* Pompe e valvole risparmiate (solo Lubrifica) */}
      {isLubrifica && (richiesta.pompeRisparmiate != null || richiesta.valvoleRisparmiate != null) && (
        <>
          <SectionTitle>
            {t("richiestaPreventivoDetailDialog.sezioni.attrezzaturaRisparmiata")}
          </SectionTitle>
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.pompeNonSostituite")}
            value={formatNumber(richiesta.pompeRisparmiate, formatter)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.valvoleNonSostituite")}
            value={formatNumber(richiesta.valvoleRisparmiate, formatter)}
            emptyLabel={ndLabel}
          />
        </>
      )}
      
      {/* Risultati calcolati */}
      <SectionTitle>
        {t("richiestaPreventivoDetailDialog.sezioni.risultatiCalcolati")}
      </SectionTitle>

      {isLubrifica && (
        <>
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.olioNonAcquistato")}
            value={formatLitri(richiesta.olioNonAcquistato, formatter)}
            emptyLabel={ndLabel}
          />
          {richiesta.risparmioEconomicoPompe != null && (
            <DetailRow
              label={t("richiestaPreventivoDetailDialog.campi.risparmioEconomicoPompe")}
              value={formatEuro(richiesta.risparmioEconomicoPompe, formatter)}
              emptyLabel={ndLabel}
            />
          )}
          {richiesta.risparmioEconomicoValvole != null && (
            <DetailRow
              label={t("richiestaPreventivoDetailDialog.campi.risparmioEconomicoValvole")}
              value={formatEuro(richiesta.risparmioEconomicoValvole, formatter)}
              emptyLabel={ndLabel}
            />
          )}
          {richiesta.risparmioEconomicoFermoMacchina != null && (
            <DetailRow
              label={t("richiestaPreventivoDetailDialog.campi.risparmioEconomicoFermoMacchina")}
              value={formatEuro(richiesta.risparmioEconomicoFermoMacchina, formatter)}
              emptyLabel={ndLabel}
            />
          )}
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.risparmioEconomico")}
            value={formatEuro(richiesta.risparmioEconomicoLubrifica, formatter)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.co2Risparmiata")}
            value={formatCo2(richiesta.co2Risparmiata)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.alberiEquivalenti")}
            value={formatNumber(richiesta.numeriAlberiSalvati, formatter)}
            emptyLabel={ndLabel}
          />
        </>
      )}

      {isLavorazioni && (
        <>
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.litriNonAcquistati")}
            value={formatLitri(
              richiesta.litriNonAcquistatiLavorazioni,
              formatter
            )}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.risparmioAcquistoFluido")}
            value={formatEuro(richiesta.risparmioEconomicoFluido, formatter)}
            emptyLabel={ndLabel}
          />
          {isLubrorefrigerante && (
            <>
              <DetailRow
                label={
                  t("richiestaPreventivoDetailDialog.campi.litriEmulsioneNonSmaltiti")
                }
                value={formatLitri(
                  richiesta.litriEmulsioneNonSmaltita,
                  formatter
                )}
                emptyLabel={ndLabel}
              />
              <DetailRow
                label={t("richiestaPreventivoDetailDialog.campi.risparmioSmaltimento")}
                value={formatEuro(richiesta.risparmioSmaltimento, formatter)}
                emptyLabel={ndLabel}
              />
            </>
          )}
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.risparmioUtensili")}
            value={formatEuro(richiesta.risparmioUtensili, formatter)}
            emptyLabel={ndLabel}
          />
          {richiesta.risparmioEconomicoPompeAltaPressione != null && (
            <DetailRow
              label={t("richiestaPreventivoDetailDialog.campi.risparmioEconomicoPompeAltaPressione")}
              value={formatEuro(richiesta.risparmioEconomicoPompeAltaPressione, formatter)}
              emptyLabel={ndLabel}
            />
          )}
          <DetailRow
            label={
              t("richiestaPreventivoDetailDialog.campi.risparmioEconomicoLavorazioni")
            }
            value={formatEuro(
              richiesta.risparmioEconomicoLavorazioniMeccaniche,
              formatter
            )}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.co2Evitata")}
            value={formatCo2(richiesta.co2EvitataLavorazioni)}
            emptyLabel={ndLabel}
          />
          <DetailRow
            label={t("richiestaPreventivoDetailDialog.campi.equivalenzaAlberi")}
            value={formatNumber(richiesta.equivalenzaAlberiLavorazioni, formatter)}
            emptyLabel={ndLabel}
          />
        </>
      )}

      {/* Date */}
      <SectionTitle>
        {t("richiestaPreventivoDetailDialog.sezioni.date")}
      </SectionTitle>

      <DetailRow
        label={t("richiestaPreventivoDetailDialog.campi.dataRichiesta")}
        value={
          richiesta.createdAt
            ? new Date(richiesta.createdAt).toLocaleDateString(formatter.locale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : ndLabel
        }
        emptyLabel={ndLabel}
      />
      <DetailRow
        label={t("richiestaPreventivoDetailDialog.campi.ultimoAggiornamento")}
        value={
          richiesta.updatedAt
            ? new Date(richiesta.updatedAt).toLocaleDateString(formatter.locale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : ndLabel
        }
        emptyLabel={ndLabel}
      />
    </Grid>
  );
}
